import type { AsyncActionAPI } from 'src/replicant/config/actions/createSharedStatesActions';
import { TeamEventStatus, getCurrentTeamEventId } from '../team.getters';
import { TeamComputedProperties } from '../team.properties';
import { TeamState } from '../team.schema';
import { MIN_MEMBERS_TO_JOIN_COMPETITION } from './teamCompetition.ruleset';

/**
 * Check if the user can participate on team competition.
 * @param state
 */
export function canParticipateOnTeamCompetition(
  team: TeamState | TeamComputedProperties,
): boolean {
  // Ignore min members check in development mode.
  if (process.env.IS_DEVELOPMENT) {
    return true;
  }

  const memberCount =
    'global' in team
      ? Object.keys(team.global.members).length
      : team.memebersCount;
  if (memberCount < MIN_MEMBERS_TO_JOIN_COMPETITION) {
    return false;
  }

  return true;
}

/**
 * Get current team competition status, to determine if the user can claim rewards.
 */
export function getTeamCompetitionEventStatus(args: {
  team: TeamState;
  userId: string;
  eventId: string;
  now: number;
}): TeamEventStatus {
  const member = args.team.global.members[args.userId];
  const contribution = member.contributions[args.eventId];
  const currentEventId = getCurrentTeamEventId(args.now);

  // Check event is still in progress.
  if (args.eventId === currentEventId) {
    return 'in-progress';
  }

  // Check if user has contributed to the event.
  if (!contribution || !contribution.score) {
    return 'complete-no-contribution';
  }

  // Check if user has already claimed the rewards.
  if (contribution.claimedRewardsAt) {
    return 'complete-already-claimed';
  }

  // Passed all checks, rewards are available.
  return 'complete-rewards-available';
}

/**
 * Get team events analytics data.
 * @param teamId
 * @param competitionId
 * @param teamState
 * @param opponentsIds
 * @param rawApi
 * @returns Analytics data.
 */
export async function getTeamEventAnalytics(
  teamId: string,
  competitionId: string,
  teamState: TeamState,
  opponentsIds: string[],
  api: AsyncActionAPI,
): Promise<{
  teamId: string;
  tournamentUniqueId: string;
  opponentCount: number;
  memberCount: number;
  avgOpponentSize: number;
}> {
  const opponents = await api.sharedStates.team.search({
    where: {
      id: {
        isOneOf: opponentsIds,
      },
    },
    limit: opponentsIds.length,
  });
  const avgOpponentSize = opponents.results.reduce((acc, curr) => {
    return acc + curr.memebersCount / opponents.results.length;
  }, 0);

  return {
    teamId,
    tournamentUniqueId: competitionId,
    opponentCount: opponentsIds.length,
    memberCount: Object.keys(teamState?.global.members ?? {}).length,
    avgOpponentSize,
  };
}
